import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import usmApi from "@/lib/usmApi";
import { useUsmAuth } from "@/contexts/UsmAuthContext";
import { PageHeader } from "../UsmLayout";
import { toast } from "sonner";
import { Banknote, CreditCard, ArrowDownToLine, Calendar, Eye } from "lucide-react";

const TRY = new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", minimumFractionDigits: 2 });
const today = () => new Date().toISOString().slice(0, 10);

export default function CashClose() {
  const { employee, isSuperAdmin } = useUsmAuth();
  const navigate = useNavigate();
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState(isSuperAdmin ? "" : (employee?.branch_id || ""));
  const [businessDay, setBusinessDay] = useState(today());
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actualCash, setActualCash] = useState("");
  const [openingFloat, setOpeningFloat] = useState("0");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isSuperAdmin) {
      usmApi.get("/branches").then((r) => {
        const list = r.data || [];
        setBranches(list);
        // Nudge: auto-select the first active branch so the cashier sees today's
        // breakdown immediately without having to pick from the dropdown.
        if (list.length && !branchId) {
          const first = list.find((b) => b.is_active) || list[0];
          if (first) setBranchId(first.id);
        }
      }).catch(() => {});
    }
    // eslint-disable-next-line
  }, [isSuperAdmin]);

  useEffect(() => {
    if (!branchId) { setPreview(null); return; }
    setLoading(true);
    const qs = new URLSearchParams({ branch_id: branchId, business_day: businessDay });
    usmApi.get(`/cash-close/preview?${qs.toString()}`)
      .then((r) => setPreview(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Önizleme alınamadı"))
      .finally(() => setLoading(false));
  }, [branchId, businessDay]);

  const expectedCash = useMemo(() => {
    if (!preview) return 0;
    return Math.max(0, (Number(preview.expected_cash) || 0) + (Number(openingFloat) || 0));
  }, [preview, openingFloat]);

  const diff = useMemo(() => (Number(actualCash) || 0) - expectedCash, [actualCash, expectedCash]);
  const diffStatus = Math.abs(diff) < 0.01 ? "balanced" : diff > 0 ? "over" : "short";

  const submit = async () => {
    if (!branchId) return toast.error("Şube seçin");
    if (actualCash === "" || Number(actualCash) < 0) return toast.error("Geçerli bir nakit miktarı girin");
    setSaving(true);
    try {
      const r = await usmApi.post("/cash-close", {
        branch_id: branchId,
        business_day: businessDay,
        actual_cash: Number(actualCash),
        opening_float: Number(openingFloat) || 0,
        note: note || null,
      });
      toast.success(`Kasa kapanışı ${r.data.cash_close_no} kaydedildi`);
      navigate(`/usm/cash-close/${r.data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Kapanış başarısız"); }
    finally { setSaving(false); }
  };

  const breakdown = preview?.breakdown?.by_type || { cash: 0, card: 0, transfer: 0, other: 0 };

  return (
    <div data-testid="usm-cash-close">
      <PageHeader
        title="Kasa Kapanış"
        breadcrumbs={[{ label: "Panel", to: "/usm" }, { label: "Kasa Kapanış" }]}
        actions={<Link to="/usm/cash-close/history" data-testid="cash-history-link"
          className="text-xs uppercase tracking-[0.2em] px-4 py-2.5 border border-[#E7E5E4] hover:bg-[#FAFAF9] inline-flex items-center gap-2">
          <Calendar size={14} /> Geçmiş
        </Link>}
      />

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-4">
        {/* Left: today's breakdown */}
        <section className="bg-white border border-[#E7E5E4] p-5">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            {isSuperAdmin && (
              <select data-testid="cash-branch" value={branchId} onChange={(e) => setBranchId(e.target.value)} className="px-3 py-2 border border-[#E7E5E4] text-sm flex-1">
                <option value="">Şube seçin...</option>
                {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            )}
            <input data-testid="cash-day" type="date" value={businessDay} max={today()}
              onChange={(e) => setBusinessDay(e.target.value)}
              className="px-3 py-2 border border-[#E7E5E4] text-sm" />
          </div>

          {loading ? <div className="text-center py-10 text-[#71717A] text-sm">Yükleniyor...</div>
            : !preview ? <div className="text-center py-10 text-[#A8A29E] text-sm">Şube ve gün seçin.</div>
            : (
              <>
                <h3 className="text-[11px] uppercase tracking-[0.2em] text-[#71717A] mb-3">Bugünkü Satışlar</h3>
                <div className="grid grid-cols-2 gap-3 mb-5">
                  <PayTile testid="break-cash"    icon={Banknote}        label="Nakit"       amount={breakdown.cash}     primary />
                  <PayTile testid="break-card"    icon={CreditCard}      label="Kredi Kartı" amount={breakdown.card} />
                  <PayTile testid="break-transfer" icon={ArrowDownToLine} label="Havale/EFT" amount={breakdown.transfer} />
                  <PayTile testid="break-other"   icon={ArrowDownToLine} label="Diğer"      amount={breakdown.other} />
                </div>
                <div className="border-t border-[#E7E5E4] pt-3 flex items-center justify-between">
                  <span className="text-[11px] uppercase tracking-[0.15em] text-[#71717A]">Toplam İşlem</span>
                  <span data-testid="break-tx-count" className="font-finance text-lg">{preview.breakdown.tx_count}</span>
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-[11px] uppercase tracking-[0.15em] text-[#71717A]">Brüt Satış</span>
                  <span data-testid="break-gross" className="font-finance text-lg">{TRY.format(preview.breakdown.gross)}</span>
                </div>

                {preview.existing_close && (
                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 text-amber-900 text-sm">
                    Bu gün için kasa kapanışı zaten yapılmış:
                    <Link to={`/usm/cash-close/${preview.existing_close.id}`} className="ml-2 underline">
                      {preview.existing_close.cash_close_no}
                    </Link>
                  </div>
                )}
              </>
            )}
        </section>

        {/* Right: variance + submit */}
        <aside className="bg-white border border-[#E7E5E4] p-5">
          <h3 className="text-[11px] uppercase tracking-[0.2em] text-[#71717A] mb-3">Sayım</h3>
          <Field label="Açılış Kasası (TRY)"><input data-testid="cash-opening" type="number" step="0.01" min="0" value={openingFloat} onChange={(e) => setOpeningFloat(e.target.value)} className={inputCls} /></Field>
          <Field label="Sayılan Gerçek Nakit (TRY) *">
            <input data-testid="cash-actual" type="number" step="0.01" min="0" autoFocus value={actualCash}
              onChange={(e) => setActualCash(e.target.value)}
              placeholder="0,00"
              className={inputCls + " text-lg font-finance"} />
          </Field>
          <Field label="Not"><textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} className={inputCls + " resize-none"} /></Field>

          <div className="border-t border-[#E7E5E4] my-4" />

          <Row label="Beklenen Nakit" value={TRY.format(expectedCash)} testid="row-expected" />
          <Row label="Sayılan Nakit"  value={TRY.format(Number(actualCash) || 0)} testid="row-actual" />
          <Row label="Fark"           value={(diff > 0 ? "+" : "") + TRY.format(diff)} testid="row-diff"
            cls={diffStatus === "balanced" ? "text-emerald-700" : diffStatus === "over" ? "text-blue-700" : "text-red-600"} bold />

          <div className={"mt-2 text-[11px] uppercase tracking-[0.15em] text-center py-2 border " +
            (diffStatus === "balanced" ? "border-emerald-200 bg-emerald-50 text-emerald-700"
              : diffStatus === "over" ? "border-blue-200 bg-blue-50 text-blue-700"
              : "border-red-200 bg-red-50 text-red-600")}>
            {diffStatus === "balanced" ? "✓ Denk" : diffStatus === "over" ? "↑ Fazla" : "↓ Eksik"}
          </div>

          <button data-testid="cash-submit" onClick={submit} disabled={saving || !branchId || actualCash === "" || preview?.existing_close}
            className="mt-4 w-full bg-[#0A0A0A] disabled:opacity-40 text-white text-xs uppercase tracking-[0.2em] py-3.5">
            {saving ? "Kaydediliyor..." : "Kasa Kapanışını Kaydet"}
          </button>
        </aside>
      </div>
    </div>
  );
}

function PayTile({ testid, icon: Icon, label, amount, primary }) {
  return (
    <div className={"border p-3 " + (primary ? "border-[#C8A96A] bg-[#FBF5E8]" : "border-[#E7E5E4] bg-white")} data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">{label}</span>
        <Icon size={14} className="text-[#C8A96A]" strokeWidth={1.5} />
      </div>
      <div className={"font-finance " + (primary ? "text-xl text-[#0A0A0A]" : "text-base text-[#52525B]")}>
        {TRY.format(amount || 0)}
      </div>
    </div>
  );
}

function Row({ label, value, cls = "", bold = false, testid }) {
  return (
    <div className="flex items-center justify-between text-sm py-1">
      <span className="text-[#71717A]">{label}</span>
      <span data-testid={testid} className={"font-finance " + (bold ? "text-lg " : "") + cls}>{value}</span>
    </div>
  );
}
function Field({ label, children }) {
  return <div className="mb-3"><label className="text-[11px] uppercase tracking-[0.15em] text-[#71717A] block mb-1.5">{label}</label>{children}</div>;
}
const inputCls = "w-full px-3 py-2.5 border border-[#E7E5E4] focus:border-[#C8A96A] outline-none text-sm";
